import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export type Membership = {
  schoolId: string;
  schoolName: string;
  role: 'director' | 'teacher';
};

interface State {
  memberships: Membership[];
  loading: boolean;
  error: string | null;
}

/**
 * Returns all (school, role) tuples the current user belongs to.
 * The first one (most recent) is treated as the active school by callers.
 */
export function useMembership(): State {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState<State>({
    memberships: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setState({ memberships: [], loading: false, error: null });
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase()
        .from('school_members')
        .select('role, school_id, schools(name)')
        .eq('user_id', user.id)
        .not('accepted_at', 'is', null);

      if (cancelled) return;
      if (error) {
        setState({ memberships: [], loading: false, error: error.message });
        return;
      }
      type Row = {
        role: 'director' | 'teacher';
        school_id: string;
        schools: { name: string } | null;
      };
      const rows = (data ?? []) as unknown as Row[];
      const memberships: Membership[] = rows.map((m) => ({
        schoolId: m.school_id,
        schoolName: m.schools?.name ?? 'École',
        role: m.role,
      }));
      setState({ memberships, loading: false, error: null });
    })();
    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  return state;
}
